import { createContext, useEffect, useReducer, useContext, useRef } from 'react';
import PropTypes from 'prop-types';

// utils
import axios from '../utils/axios';
import { setSession } from '../utils/jwt';
import { clearAllLastEventIds, getLastEventIdForTopic } from '../utils/socketReplayCursor';
import { SocketContext } from './SocketIo';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  UPDATE: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      user,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  update: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const socket = useContext(SocketContext);
  const reconnectTimerRef = useRef(null);

  // Starts every time you enter the page
  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const userId = window.localStorage.getItem('usr_id');

        if (accessToken && userId && accessToken !== undefined) {
          setSession(accessToken);

          const response = await axios.get(`/api/user/${userId}`);
          const user = response.data;
          user.accessToken = accessToken;

          // CheckCompany
          const companyRes = await axios.get(`/api/company/${user.companyId}`);

          if (companyRes.status) {
            user.company = companyRes.data;
          }
          // CheckService
          const serviceRes = await axios.post(`/api/service/query/`, {
            query: { companyId: user.companyId },
            projection: { description: 1, type: 1, _id: 1 },
          });

          if (serviceRes.status) {
            user.services = serviceRes.data;
          }

          // Fetch feature flags
          const featureFlagRes = await axios.get(`/api/feature-flags`);
          if (featureFlagRes.status === 200) {
            user.featureFlags = featureFlagRes.data;
          } else {
            user.featureFlags = {};
          }

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
        // Clean data about last login
        removeAllData();
      }
    };
    initialize();
  }, []);

  // Single socket lifecycle for both bootstrap and login.
  //
  // Why two reconnection paths:
  // 1) Built-in Socket.IO reconnection (configured in SocketIo.js) handles transient
  //    network interruptions and transport hiccups.
  // 2) Manual reconnect below handles `io server disconnect`, a case where the
  //    client does NOT auto-reconnect by default in Socket.IO.
  useEffect(() => {
    const companyId = state.user?.companyId;

    if (!state.isAuthenticated || !companyId) {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      clearAllLastEventIds();

      // Ensure no authenticated socket remains alive after logout/session loss.
      if (socket.connected) {
        socket.close();
      }

      return undefined;
    }

    const accessToken = window.localStorage.getItem('accessToken');
    if (!accessToken) {
      throw new Error('[JWTContext] Missing accessToken while user is authenticated.');
    }

    socket.auth = { token: accessToken };

    const onConnect = () => {
      const lastEventId = getLastEventIdForTopic(companyId);
      socket.emit('subscribe', { topic: companyId, lastEventId });
    };

    const onDisconnect = (reason) => {
      // Socket.IO does not auto-reconnect after `io server disconnect`.
      if (reason !== 'io server disconnect') return;

      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
      }

      reconnectTimerRef.current = window.setTimeout(() => {
        socket.connect();
      }, Math.random() * 5000);
    };

    socket.off('connect', onConnect);
    socket.off('disconnect', onDisconnect);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    if (!socket.connected) {
      socket.connect();
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);

      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [socket, state.isAuthenticated, state.user?.companyId]);

  const login = async (email, password, forceLogout = false) => {
    const response = await axios.post('/api/login', {
      email,
      password,
      forceLogout,
    });

    const { token, user } = response.data;
    window.Sentry?.setUser({ email: user.email, id: user._id, username: user.name });

    // Set token before login
    axios.defaults.headers.Authorization = `Bearer ${token}`;
    // CheckCompany
    const companyRes = await axios.get(`/api/company/${user.companyId}`);

    if (companyRes.status) {
      user.company = companyRes.data;
    }

    user.accessToken = token;

    const featureFlagRes = await axios.get(`/api/feature-flags`);
    if (featureFlagRes.status === 200) {
      user.featureFlags = featureFlagRes.data;
    } else {
      user.featureFlags = {};
    }

    setSession(token);
    window.localStorage.setItem('usr_id', user._id);

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);
    window.localStorage.setItem('usr_id', user._id);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    const userId = window.localStorage.getItem('usr_id');
    await axios.get('/api/logout');
    socket.emit('immediate-disconnect', userId);
    socket.close();

    window.Sentry?.setUser(null);

    setSession(null);
    dispatch({ type: 'LOGOUT' });
    removeAllData();
  };
  // Clean data in storage
  const removeAllData = () => {
    setSession(null);
    window.localStorage.clear();
  };

  const update = (user) => {
    dispatch({
      type: 'UPDATE',
      payload: {
        user,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        update,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
